/*
 ** 选购商品 & 结算 抽屉
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { Spin, Button, message } from 'antd';

import { orderBillNotPay, orderBillPay } from '@/api/goods';
import { DrawerHeader, DrawerFooter } from '@/components/PortalDrawer';
import PortalLoading from '@/components/PortalLoading';
import { PayModalRef, PayWay } from '@/components/Payment/interface';
import { renderIntlLabel } from '@/utils';

import ChooseGoods from './ChooseGoods/index';
import SettleGoods from './SettleGoods/index';
import usePortalDrawer from './PortalDrawer';

import { stepEnumKey, titleTextMapKey, preOrderHandle } from './help';

import styles from './index.module.scss';

/**
 * @param IConfig 参数
 * @example shoppingPortal.showDrawer({ recNo, resvNo, roomId, roomNo, certNo, name, callback });
 */

interface IConfig {
  recNo: string; // 订单号
  resvNo: string; // 子订单号
  roomId: number; // 房间id
  roomNo: string; // 房间编号
  roomStatus: number | null; // 房间状态
  certNo: string; // 房主身份证信息
  wyuid: string;
  name: string; // 房主姓名
  callback?: () => void; // 确认按钮的回调函数
}

interface IProps extends IConfig {
  cartList: any;
  actualPay: number;
  unmountNode: () => void;
}

interface IFooterBtns {
  step: stepEnumKey;
  disabledNext: boolean;
  onCancel: () => void;
  onNext: () => void;
  onPrev: () => void;
  onOk: () => void;
}

// 弹框底部按钮
function FooterBtnGroups({ step, disabledNext, onCancel, onNext, onPrev, onOk }: IFooterBtns) {
  if (step === stepEnumKey.first)
    return (
      <>
        <Button type="primary" disabled={disabledNext} onClick={onNext}>
          {renderIntlLabel('goods_action_next_step')}
        </Button>
        <Button type="default" onClick={onCancel}>
          {renderIntlLabel('goods_action_cancel')}
        </Button>
      </>
    );
  return (
    <>
      <Button type="primary" onClick={onOk}>
        {renderIntlLabel('goods_action_confirm')}
      </Button>
      <Button type="default" onClick={onPrev}>
        {renderIntlLabel('goods_action_pre_step')}
      </Button>
    </>
  );
}

// 弹框
const Index: React.FC<IConfig> = ($$: IProps) => {
  const [modalState, setModalState] = useState({
    isInit: false,
    isLoading: false,
    step: stepEnumKey.first,
    orderNo: null,
    paymentTypes: 1, // 支付类型 0-入账并直接支付 1-先入帐，稍后统一结账（默认）
  });

  // Payment & PayModal props
  const payModalRef = useRef<PayModalRef>();
  const [payType, setPayType] = useState(1); // 支付方式(3:扫码支付, 2: 会员卡扣, 1:现金)

  // payType changed callback
  const selectPayType = (type) => {
    setPayType(type);
  };

  // pay result callback
  const payResultCallback = (res: {
    generatePayOrder?: boolean;
    payed: boolean;
    result: boolean;
  }) => {
    if (res.generatePayOrder) {
      $$.callback();
      return;
    }

    // pay result, do something
    const { payed, result } = res || {};

    if (!payed && !result) {
      // just close, do nothing
      return;
    }

    // 没有支付成功 或者入账购买失败
    if (!payed || !result)
      message.error(renderIntlLabel('goods_msg_goods_credited_unsuccessfully'));

    // 支付成功
    if (payed && result) {
      // 前台直售
      message.success(renderIntlLabel('goods_msg_goods_credited_successfully'));

      // 调用回调刷新订单列表
      $$.callback();

      // 关闭弹框
      $$.unmountNode();
    }
  };

  const updateModalState = useCallback<(state: Partial<typeof modalState>) => void>((state) => {
    setModalState((prevState) => ({ ...prevState, ...state }));
  }, []);

  // mounted
  useEffect(() => {
    updateModalState({ isInit: true });
  }, []);

  // title render
  const renderTitle = () => {
    return <span className={styles.modalTitle}>{titleTextMapKey[modalState.step]}</span>;
  };

  // 支付类型选择
  const payRadioChange = (paymentTypes: 0 | 1) => {
    updateModalState({ paymentTypes });
  };

  // 预下单
  const nextHandle = async () => {
    try {
      updateModalState({ isLoading: true });
      const { code, orderNo } = await preOrderHandle($$, modalState.orderNo);
      if (code === 0) updateModalState({ step: stepEnumKey.second, orderNo, isLoading: false });
    } catch (error) {
      updateModalState({ isLoading: false });
    }
  };

  // 先入账，稍后统一结账
  const noPayIntoOrder = async () => {
    try {
      updateModalState({ isLoading: true });
      const { code } = await orderBillNotPay({
        orderNo: modalState.orderNo,
        trigger: '通过订单商品售卖入账时',
        operationPage: '订单商品直售抽屉',
      });
      if (code === 0) {
        message.success(renderIntlLabel('goods_msg_goods_credited_successfully'));
        $$.unmountNode();
        $$.callback();
      }
      updateModalState({ isLoading: false });
    } catch (error) {
      updateModalState({ isLoading: false });
    }
  };

  // 入账并直接支付
  const gotoPay = async (confirm?: boolean) => {
    // 付款单需confirm后下单
    if (!confirm && payType === PayWay.Fukuandan) {
      payModalRef.current.openModal({
        payType,
        showFukuanDanConfirm: true,
      });
      return;
    }

    try {
      updateModalState({ isLoading: true });
      // 获取支付单号
      const { code, data } = await orderBillPay({
        orderNo: modalState.orderNo,
        payPrice: $$.actualPay,
        payType: 103,
        fkdFlag: payType === PayWay.Fukuandan,
      });
      if (code === 0) {
        payModalRef.current.openModal({
          payAmount: $$.actualPay * 100,
          payType,
          payOrderType: 103,
          paymentNo: data,
          certNo: $$.certNo,
          wyuid: $$.wyuid,
          businessType: 1,
          extraParams: {
            trigger: '通过订单商品售卖入账时',
            operationPage: '订单商品直售抽屉',
          },
        });
      }
      updateModalState({ isLoading: false });
    } catch (error) {
      updateModalState({ isLoading: false });
    }
  };

  // 确定
  const okHandle = (confirm?: boolean) => {
    // 先入账后统一结账
    if (modalState.paymentTypes === 1) {
      noPayIntoOrder();
      return;
    }
    // 直接支付
    gotoPay(confirm);
  };

  if (!modalState.isInit) return <PortalLoading />;

  return (
    <Spin spinning={modalState.isLoading}>
      <DrawerHeader title={renderTitle()} onClose={$$.unmountNode} />
      <ChooseGoods step={modalState.step} />
      <SettleGoods
        step={modalState.step}
        orderNo={modalState.orderNo}
        roomStatus={$$.roomStatus}
        paymentTypes={modalState.paymentTypes}
        payRadioChange={payRadioChange}
        // 支付相关
        payModalRef={payModalRef}
        setPayType={setPayType}
        selectPayType={selectPayType}
        payResultCallback={payResultCallback}
        onConfirmOk={() => okHandle(true)}
        {...$$}
      />
      <DrawerFooter>
        <FooterBtnGroups
          step={modalState.step}
          disabledNext={$$.cartList.size === 0}
          onCancel={$$.unmountNode}
          onNext={nextHandle}
          onPrev={() => {
            updateModalState({ step: stepEnumKey.first });
          }}
          onOk={() => okHandle()}
        />
      </DrawerFooter>
    </Spin>
  );
};

const mapStateToProps = (state: any) => {
  return {
    ...state.goods,
  };
};

export default () => usePortalDrawer<IConfig>(connect(mapStateToProps, {})(React.memo(Index)));
